import React, { useState, useCallback, useEffect } from 'react';
import {
    X, ArrowLeft, CalendarPlus, Trash2, Phone, Mail,
    Clock, Scissors, Plus, Save, AlertCircle
} from 'lucide-react';
import { API_BASE_URL } from '../../config';
import { formatAppointmentDateShort, getLocalDateString, getTimeSlotDisplayItems } from '../../utils/dateUtils';
import './MultiAppointmentModal.css';

const timeToMinutes = (time24) => {
    const parts = (time24 || '09:00').split(':');
    return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0);
};

const hasLocalConflict = (queue, barberId, date, time24, duration) => {
    const start = timeToMinutes(time24);
    const end = start + (duration || 30);
    return queue.some((item) => {
        if (item.barber.id !== barberId || item.date !== date) return false;
        const existingStart = timeToMinutes(item.time);
        const existingEnd = existingStart + (item.customDuration ?? item.service.duration_minutes ?? 30);
        return start < existingEnd && end > existingStart;
    });
};

const mapQueueItemToPayload = (item) => ({
    barber_id: item.barber.id,
    service_id: item.service.id || null,
    service_name: item.service.name,
    appointment_date: item.date,
    appointment_time: item.time,
    duration_minutes: item.customDuration ?? item.service.duration_minutes ?? 30,
    price: item.service.price ?? null,
    notes: '',
});

const MultiAppointmentModal = ({
    isOpen,
    onClose,
    client,
    barbers = [],
    serviceModules = [],
    onSuccess,
}) => {
    const [step, setStep] = useState('barber');
    const [currentBarber, setCurrentBarber] = useState(null);
    const [currentService, setCurrentService] = useState(null);
    const [barberServices, setBarberServices] = useState([]);
    const [currentDate, setCurrentDate] = useState('');
    const [currentTime, setCurrentTime] = useState('');
    const [currentDuration, setCurrentDuration] = useState(30);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [queue, setQueue] = useState([]);
    const [loadingServices, setLoadingServices] = useState(false);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const resetPicker = useCallback(() => {
        setStep('barber');
        setCurrentBarber(null);
        setCurrentService(null);
        setBarberServices([]);
        setCurrentDate('');
        setCurrentTime('');
        setCurrentDuration(30);
        setAvailableSlots([]);
        setError('');
    }, []);

    const loadBarberServices = useCallback(async (barber) => {
        setLoadingServices(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const url = `${API_BASE_URL}/admin/barbers/${barber.id}/services`;
            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const services = await res.json();
                setBarberServices(services || []);
                if (!services?.length) {
                    setError('Este barbero no tiene servicios asignados.');
                }
                return services || [];
            }
            setError('No se pudieron cargar los servicios del barbero.');
            return [];
        } catch {
            setError('Error de conexión al cargar servicios.');
            return [];
        } finally {
            setLoadingServices(false);
        }
    }, []);

    /** Tras agregar una cita o pulsar "Agregar otra cita", continúa con el mismo barbero. */
    const resumeWithBarber = useCallback(async (barber) => {
        if (!barber) {
            resetPicker();
            return;
        }
        setCurrentBarber(barber);
        setCurrentService(null);
        setCurrentDate('');
        setCurrentTime('');
        setCurrentDuration(30);
        setAvailableSlots([]);
        setError('');
        await loadBarberServices(barber);
        setStep('service');
    }, [loadBarberServices, resetPicker]);

    const handleAddAnother = () => {
        const lastItem = queue[queue.length - 1];
        resumeWithBarber(lastItem?.barber);
    };

    const handleClose = () => {
        resetPicker();
        setQueue([]);
        onClose();
    };

    const handleSelectBarber = async (barber) => {
        setCurrentBarber(barber);
        setCurrentService(null);
        setCurrentDate('');
        setCurrentTime('');
        setCurrentDuration(30);
        setAvailableSlots([]);
        setError('');
        await loadBarberServices(barber);
        setStep('service');
    };

    const handleSelectService = (service) => {
        setCurrentService(service);
        setCurrentDate('');
        setCurrentTime('');
        setCurrentDuration(service.duration_minutes || 30);
        setAvailableSlots([]);
        setError('');
        setStep('datetime');
    };

    const handleDateChange = async (date, durationOverride) => {
        setCurrentDate(date);
        setCurrentTime('');
        setError('');
        if (!date || !currentBarber || !currentService) return;
        setLoadingSlots(true);
        try {
            const token = localStorage.getItem('token');
            const duration = durationOverride ?? currentDuration;
            const url = `${API_BASE_URL}/barber/availability?barber_id=${currentBarber.id}&date_str=${date}&duration=${duration}&use_extended=true`;
            const res = await fetch(url, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (res.ok) {
                setAvailableSlots(await res.json());
            } else {
                setAvailableSlots([]);
            }
        } catch {
            setAvailableSlots([]);
        } finally {
            setLoadingSlots(false);
        }
    };

    const handleAddToQueue = () => {
        if (!currentBarber || !currentService || !currentDate || !currentTime) {
            setError('Selecciona barbero, servicio, fecha y hora.');
            return;
        }
        const duration = currentDuration;
        if (hasLocalConflict(queue, currentBarber.id, currentDate, currentTime, duration)) {
            setError('Ese horario se cruza con otra cita que ya agregaste en la lista.');
            return;
        }
        const slot = availableSlots.find((s) => s.time_24 === currentTime);
        if (slot && !slot.available) {
            setError('Ese horario no está disponible.');
            return;
        }
        setQueue((prev) => [
            ...prev,
            {
                tempId: `${Date.now()}-${Math.random()}`,
                barber: currentBarber,
                service: currentService,
                date: currentDate,
                time: currentTime,
                timeDisplay: slot?.time_display || currentTime,
                customDuration: currentDuration,
            },
        ]);
        // Mantener el mismo barbero y volver a elegir servicio/fecha
        setCurrentService(null);
        setCurrentDate('');
        setCurrentTime('');
        setCurrentDuration(30);
        setAvailableSlots([]);
        setError('');
        setStep('service');
    };

    const handleRemoveFromQueue = (tempId) => {
        setQueue((prev) => prev.filter((item) => item.tempId !== tempId));
    };

    const handleSaveAll = async () => {
        const hasPendingDraft = step === 'datetime'
            && currentBarber && currentService && currentDate && currentTime;

        if (!client?.id || (queue.length === 0 && !hasPendingDraft)) return;

        if (hasPendingDraft) {
            const duration = currentDuration;
            if (hasLocalConflict(queue, currentBarber.id, currentDate, currentTime, duration)) {
                setError('Ese horario se cruza con otra cita que ya agregaste en la lista.');
                return;
            }
            const slot = availableSlots.find((s) => s.time_24 === currentTime);
            if (slot && !slot.available) {
                setError('Ese horario no está disponible.');
                return;
            }
        }

        const itemsToSave = [...queue];
        if (hasPendingDraft) {
            const slot = availableSlots.find((s) => s.time_24 === currentTime);
            itemsToSave.push({
                tempId: 'pending-draft',
                barber: currentBarber,
                service: currentService,
                date: currentDate,
                time: currentTime,
                timeDisplay: slot?.time_display || currentTime,
                customDuration: currentDuration,
            });
        }

        setSaving(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const payload = {
                client_id: client.id,
                appointments: itemsToSave.map(mapQueueItemToPayload),
            };
            const bulkUrl = `${API_BASE_URL}/admin/appointments/bulk`;
            const res = await fetch(bulkUrl, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                const created = data.created ?? itemsToSave.length;
                alert(`${created} cita(s) agendada(s) correctamente para ${client.name}`);
                handleClose();
                onSuccess?.();
            } else {
                setError(data.detail || 'Error al guardar las citas');
            }
        } catch {
            setError('Error de conexión con el servidor');
        } finally {
            setSaving(false);
        }
    };

    const formatCurrency = (amount) =>
        new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(amount || 0);

    if (!isOpen || !client) return null;

    const hasPendingDraft = step === 'datetime'
        && currentBarber && currentService && currentDate && currentTime;
    const totalToSave = queue.length + (hasPendingDraft ? 1 : 0);

    const servicesByModule = barberServices.reduce((acc, service) => {
        const moduleId = service.module_id || 'unassigned';
        let moduleName = 'Otros servicios';
        if (service.module) {
            moduleName = service.module.display_name || service.module.name;
        } else if (service.module_name) {
            moduleName = service.module_name;
        } else if (serviceModules?.length) {
            const found = serviceModules.find((m) => m.id === service.module_id);
            if (found) moduleName = found.display_name || found.name;
        }
        if (!acc[moduleId]) acc[moduleId] = { id: moduleId, name: moduleName, services: [] };
        acc[moduleId].services.push(service);
        return acc;
    }, {});

    return (
        <div className="multi-appt-overlay" onClick={handleClose}>
            <div className="multi-appt-modal" onClick={(e) => e.stopPropagation()}>
                <div className="multi-appt-header">
                    <div>
                        <h2>Agendar citas</h2>
                        <p className="multi-appt-client-name">{client.name}</p>
                    </div>
                    <button type="button" className="multi-appt-close" onClick={handleClose} aria-label="Cerrar">
                        <X size={22} />
                    </button>
                </div>

                <div className="multi-appt-client-info">
                    {client.phone && (
                        <span><Phone size={14} /> {client.phone}</span>
                    )}
                    {client.email && (
                        <span><Mail size={14} /> {client.email}</span>
                    )}
                </div>

                {queue.length > 0 && (
                    <div className="multi-appt-queue">
                        <h3>Citas a agendar ({queue.length})</h3>
                        <ul>
                            {queue.map((item) => (
                                <li key={item.tempId}>
                                    <div className="multi-appt-queue-info">
                                        <strong>{formatAppointmentDateShort(item.date)}</strong>
                                        <span>{item.timeDisplay}</span>
                                        <span>{item.barber.name}</span>
                                        <span>{item.service.name}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="multi-appt-queue-remove"
                                        onClick={() => handleRemoveFromQueue(item.tempId)}
                                        aria-label="Quitar cita"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {error && (
                    <div className="multi-appt-error">
                        <AlertCircle size={18} />
                        <span>{error}</span>
                    </div>
                )}

                <div className="multi-appt-body">
                    {step === 'barber' && (
                        <div>
                            <p className="multi-appt-step-label">1. Selecciona el barbero</p>
                            <div className="multi-appt-barbers-grid">
                                {barbers.filter((b) => b.available).map((barber) => (
                                    <button
                                        key={barber.id}
                                        type="button"
                                        className="multi-appt-barber-card"
                                        onClick={() => handleSelectBarber(barber)}
                                        disabled={loadingServices}
                                    >
                                        <div className="multi-appt-barber-avatar">
                                            {barber.name.charAt(0).toUpperCase()}
                                        </div>
                                        <span>{barber.name}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 'service' && currentBarber && (
                        <div>
                            <button type="button" className="multi-appt-back" onClick={() => setStep('barber')}>
                                <ArrowLeft size={18} /> Volver
                            </button>
                            <p className="multi-appt-step-label">
                                2. Servicio de <strong>{currentBarber.name}</strong>
                            </p>
                            {barberServices.length === 0 ? (
                                <p className="multi-appt-empty">No hay servicios disponibles.</p>
                            ) : (
                                Object.values(servicesByModule).map((group) => (
                                    <div key={group.id} className="multi-appt-service-group">
                                        <h4>{group.name}</h4>
                                        <div className="multi-appt-services-list">
                                            {group.services.map((service) => (
                                                <button
                                                    key={service.id}
                                                    type="button"
                                                    className="multi-appt-service-card"
                                                    onClick={() => handleSelectService(service)}
                                                >
                                                    <Scissors size={16} />
                                                    <div>
                                                        <span className="multi-appt-service-name">{service.name}</span>
                                                        <span className="multi-appt-service-meta">
                                                            {service.duration_minutes || 30} min · {formatCurrency(service.price)}
                                                        </span>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {step === 'datetime' && currentBarber && currentService && (
                        <div>
                            <button
                                type="button"
                                className="multi-appt-back"
                                onClick={() => { setStep('service'); setCurrentDate(''); setCurrentTime(''); setAvailableSlots([]); }}
                            >
                                <ArrowLeft size={18} /> Volver
                            </button>
                            <p className="multi-appt-step-label">
                                3. Fecha y hora — <strong>{currentService.name}</strong>
                            </p>

                            <div className="multi-appt-duration-row">
                                <label className="multi-appt-field-label">
                                    <Clock size={14} /> Duración del servicio (min)
                                </label>
                                <input
                                    type="number"
                                    className="multi-appt-duration-input"
                                    value={currentDuration}
                                    min={5}
                                    max={240}
                                    step={5}
                                    onChange={(e) => {
                                        const parsed = parseInt(e.target.value, 10);
                                        const val = isNaN(parsed) ? '' : parsed;
                                        setCurrentDuration(val);
                                        setCurrentTime('');
                                        if (val >= 5 && currentDate) handleDateChange(currentDate, val);
                                    }}
                                    onBlur={(e) => {
                                        const val = Math.max(5, parseInt(e.target.value, 10) || 5);
                                        setCurrentDuration(val);
                                        if (currentDate) handleDateChange(currentDate, val);
                                    }}
                                />
                            </div>

                            <label className="multi-appt-field-label">Fecha</label>
                            <input
                                type="date"
                                className="multi-appt-date-input"
                                value={currentDate}
                                min={getLocalDateString()}
                                onChange={(e) => handleDateChange(e.target.value)}
                            />

                            {currentDate && (
                                <div className="multi-appt-slots-wrap">
                                    <label className="multi-appt-field-label">Hora disponible</label>
                                    {loadingSlots ? (
                                        <p className="multi-appt-loading">Cargando horarios...</p>
                                    ) : availableSlots.length === 0 ? (
                                        <p className="multi-appt-empty">No hay horarios para esta fecha.</p>
                                    ) : (
                                        <div className="multi-appt-slots-grid">
                                            {getTimeSlotDisplayItems(availableSlots, 5).map((item, idx) =>
                                                item.type === 'available' ? (
                                                    <button
                                                        key={idx}
                                                        type="button"
                                                        className={`multi-appt-slot-btn${currentTime === item.slot.time_24 ? ' is-selected' : ''}`}
                                                        onClick={() => { setCurrentTime(item.slot.time_24); setError(''); }}
                                                    >
                                                        {item.slot.time_display}
                                                    </button>
                                                ) : (
                                                    <div key={idx} className="multi-appt-slot-occupied">
                                                        {item.startDisplay} - {item.endDisplay} {item.label || 'Ocupado'}
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <button
                                type="button"
                                className="multi-appt-add-btn"
                                onClick={handleAddToQueue}
                                disabled={!currentDate || !currentTime}
                            >
                                <Plus size={18} />
                                Agregar cita a la lista
                            </button>
                        </div>
                    )}
                </div>

                <div className="multi-appt-footer">
                    {queue.length > 0 && (
                        <button type="button" className="multi-appt-add-more" onClick={handleAddAnother}>
                            <CalendarPlus size={18} />
                            Agregar otra cita
                            {queue[queue.length - 1]?.barber?.name && (
                                <span className="multi-appt-add-more-hint">
                                    ({queue[queue.length - 1].barber.name})
                                </span>
                            )}
                        </button>
                    )}
                    <button
                        type="button"
                        className="multi-appt-save-btn"
                        onClick={handleSaveAll}
                        disabled={saving || totalToSave === 0}
                    >
                        <Save size={18} />
                        {saving ? 'Guardando...' : `Guardar ${totalToSave} cita${totalToSave !== 1 ? 's' : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MultiAppointmentModal;
